import {
  Box,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  basketEndLabelKeys,
  visualLayers,
} from "components/common/plan/layerEditor/layerEditorState";
import { useTenantData } from "contexts/tenant";
import { sorted } from "helpers";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useElementSize } from "hooks/useElementSize";
import { useBasketOneMaterials } from "hooks/useMaterials";
import { Fragment } from "react";
import { type MixRead } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { ChargeProgress } from "../useHeatChargeProgress";
import {
  fitRecipeText,
  MIN_RECIPE_FONT_SIZE,
  RECIPE_COLUMN_GAP_EM,
  RECIPE_LINE_HEIGHT,
  RECIPE_PADDING_X_EM,
  RECIPE_PADDING_Y_EM,
  RecipeLayout,
  widestTextEm,
} from "./recipeFit";

const secondaryLabelSx = {
  color: "text.secondary",
} as const;

const sequenceLabelSx = {
  ...secondaryLabelSx,
  typography: "body1",
  fontWeight: "fontWeightRegular",
} as const;

const unitLabelSx = {
  ...secondaryLabelSx,
  typography: "h5",
  fontWeight: "fontWeightBold",
} as const;

const MassChargeTable = ({
  basket,
  chargeNumber,
  chargeProgress,
}: {
  basket: MixRead["baskets"][number];
  chargeNumber: number;
  chargeProgress?: ChargeProgress;
}) => {
  const { t } = useTenantTranslation();
  const {
    default_mix_breakdown,
    small_mass_per_mass,
    basket_layer_display_order,
  } = useTenantData();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const isDone =
    chargeProgress?.completedBasketNumbers.includes(basket.basket_number) ??
    false;
  const usesSmallMass = default_mix_breakdown === "small_mass";
  const massScaleFactor = usesSmallMass ? small_mass_per_mass : 1;
  const massUnit = usesSmallMass ? "small_mass" : "mass";
  const massUnitLabel = units(massUnit).trim();
  const formatMass = formatNumber(usesSmallMass ? 0 : 2, true);
  const massLayers = visualLayers(
    sorted(
      basket.layers.filter((layer) => layer.quantity.unit === "mass"),
      (layer) => layer.layer_number
    ),
    basket_layer_display_order
  );
  const endLabelKeys = basketEndLabelKeys(basket_layer_display_order);
  const totalMass = massLayers.reduce(
    (sum, layer) => sum + layer.quantity.value,
    0
  );
  const chargeLabel = `${t("charge")} ${chargeNumber.toString()}`;

  return (
    <Table
      size="small"
      aria-label={chargeLabel}
      sx={
        isDone
          ? {
              "& .MuiTableCell-root, & .MuiTypography-root, & .MuiFormControlLabel-label":
                {
                  color: "text.disabled",
                },
            }
          : undefined
      }
    >
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "fontWeightBold",
                textDecoration: isDone ? "line-through" : "none",
              }}
            >
              {chargeLabel}
            </Typography>
          </TableCell>
          <TableCell align="right">
            {chargeProgress && (
              <FormControlLabel
                label={t("done")}
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={isDone}
                    onChange={(_event, checked) => {
                      chargeProgress.setCompleted(
                        basket.basket_number,
                        checked
                      );
                    }}
                  />
                }
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    typography: "body1",
                    fontWeight: "fontWeightBold",
                  },
                }}
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={sequenceLabelSx}>{t(endLabelKeys.first)}</TableCell>
          <TableCell align="right" sx={unitLabelSx}>
            {massUnitLabel}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {massLayers.map((layer) => (
          <TableRow key={layer.layer_number} hover>
            <TableCell
              sx={{
                typography: "h5",
                fontWeight: "fontWeightMedium",
              }}
            >
              {layer.material_name}
            </TableCell>
            <TableCell
              align="right"
              sx={{
                color: isDone ? "text.disabled" : "primary.main",
                typography: "h5",
                fontWeight: "fontWeightBold",
                whiteSpace: "nowrap",
              }}
            >
              {formatMass(layer.quantity.value * massScaleFactor)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={2} sx={sequenceLabelSx}>
            {t(endLabelKeys.last)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ fontWeight: "fontWeightBold" }}>
            {t("totalMass")}
          </TableCell>
          <TableCell
            align="right"
            sx={{ fontWeight: "fontWeightBold", whiteSpace: "nowrap" }}
          >
            {formatMass(totalMass * massScaleFactor)}
            {units(massUnit)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

const MassMixDisplay = ({
  mix,
  chargeProgress,
}: {
  mix: MixRead;
  chargeProgress?: ChargeProgress;
}) => {
  const massBaskets = sorted(
    mix.baskets.filter((basket) =>
      basket.layers.some((layer) => layer.quantity.unit === "mass")
    ),
    (basket) => basket.basket_number
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 4,
        width: "100%",
      }}
    >
      {massBaskets.map((basket, index) => (
        <Box key={basket.basket_number} sx={{ minWidth: 0 }}>
          <MassChargeTable
            basket={basket}
            chargeNumber={index + 1}
            chargeProgress={chargeProgress}
          />
        </Box>
      ))}
    </Box>
  );
};

const recipeCellSx = ({
  layout,
  isLabel,
  isDimmed,
  fontSize,
}: {
  layout: RecipeLayout;
  isLabel: boolean;
  isDimmed: boolean;
  fontSize: number;
}) => {
  const paddingY = `${RECIPE_PADDING_Y_EM.toString()}em`;
  const paddingX = `${RECIPE_PADDING_X_EM.toString()}em`;
  const isInline = layout === "inline";
  return {
    m: 0,
    fontSize,
    // A long material name wraps only when the text is at its minimum size.
    whiteSpace: isLabel ? "normal" : "nowrap",
    opacity: isDimmed ? 0.5 : 1,
    pt: isInline || isLabel ? paddingY : 0,
    pb: isInline || !isLabel ? paddingY : 0,
    pl:
      isInline && !isLabel ? `${RECIPE_COLUMN_GAP_EM.toString()}em` : paddingX,
    pr: isInline && isLabel ? 0 : paddingX,
    borderBottom: isInline || !isLabel ? 1 : 0,
    borderColor: "grey.300",
    ...(isLabel
      ? { fontWeight: "fontWeightMedium" }
      : {
          fontWeight: "fontWeightBold",
          color: "primary.main",
          textAlign: isInline ? "center" : "right",
        }),
  } as const;
};

const RatioAndGrabMixDisplay = ({ mix }: { mix: MixRead }) => {
  const theme = useTheme();
  const aggregated = useBasketOneMaterials(mix);
  const { ref, size } = useElementSize();
  const ratioMaterials = aggregated.filter(
    (material) => material.unit === "ratio"
  );
  const recipeLines = [
    ...(ratioMaterials.length > 0
      ? [
          {
            label: ratioMaterials
              .map((material) => material.materialName)
              .join(":"),
            value: ratioMaterials
              .map((material) => material.totalMass)
              .join(":"),
            isDimmed: false,
          },
        ]
      : []),
    ...aggregated
      .filter((material) => material.unit === "grab")
      .map((material) => ({
        label: material.materialName,
        value: `+${material.totalMass.toString()}`,
        isDimmed: material.totalMass === 0,
      })),
  ];
  const fontFamily = theme.typography.fontFamily ?? "sans-serif";
  const { layout, labelFontSize, valueFontSize } =
    size === null
      ? {
          layout: "inline" as const,
          labelFontSize: MIN_RECIPE_FONT_SIZE,
          valueFontSize: MIN_RECIPE_FONT_SIZE,
        }
      : fitRecipeText({
          width: size.width,
          height: size.height,
          lineCount: recipeLines.length,
          widestLabelEm: widestTextEm({
            texts: recipeLines.map((line) => line.label),
            fontWeight: String(theme.typography.fontWeightMedium),
            fontFamily,
          }),
          widestValueEm: widestTextEm({
            texts: recipeLines.map((line) => line.value),
            fontWeight: String(theme.typography.fontWeightBold),
            fontFamily,
          }),
        });

  return (
    <Box ref={ref} sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gridTemplateColumns: layout === "inline" ? "1fr auto" : "1fr",
          fontFamily,
          lineHeight: RECIPE_LINE_HEIGHT,
        }}
      >
        {recipeLines.map((line) => (
          <Fragment key={line.label}>
            <Box
              component="dt"
              sx={recipeCellSx({
                layout,
                isLabel: true,
                isDimmed: line.isDimmed,
                fontSize: labelFontSize,
              })}
            >
              {line.label}
            </Box>
            <Box
              component="dd"
              sx={recipeCellSx({
                layout,
                isLabel: false,
                isDimmed: line.isDimmed,
                fontSize: valueFontSize,
              })}
            >
              {line.value}
            </Box>
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

export const MixDisplayTable = ({
  mix,
  chargeProgress,
}: {
  mix: MixRead;
  chargeProgress?: ChargeProgress;
}) => {
  const hasMassLayers = mix.baskets.some((basket) =>
    basket.layers.some((layer) => layer.quantity.unit === "mass")
  );

  if (hasMassLayers) {
    return <MassMixDisplay mix={mix} chargeProgress={chargeProgress} />;
  }

  return <RatioAndGrabMixDisplay mix={mix} />;
};
